import { collection, doc, getDoc, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from './firebase';

export const LOCAL_EXPERT_TOPIC = 'Local Area Guide';
export const GEAR_WANTED_TOPIC = 'Gear wanted';

export async function findOrStartWantedConversation({
  currentUserId,
  recipientId,
  wantedId,
  item,
  locationLabel,
  initialMessage,
}) {
  if (!currentUserId || !recipientId || !wantedId) {
    throw new Error('Missing required conversation details.');
  }

  if (currentUserId === recipientId) {
    throw new Error('You cannot message yourself.');
  }

  const messagesQuery = query(
    collection(db, 'messages'),
    where('participants', 'array-contains', currentUserId),
  );
  const snapshot = await getDocs(messagesQuery);

  let existingConvId = null;
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.conversationType !== 'gear-wanted' || data.wantedId !== wantedId) return;

    const otherUserId = data.senderId === currentUserId ? data.recipientId : data.senderId;
    if (otherUserId === recipientId) {
      existingConvId = data.conversationId || docSnap.id;
    }
  });

  if (existingConvId) return existingConvId;

  const conversationId = `conv-wanted-${wantedId}-${[currentUserId, recipientId].sort().join('-')}`;
  const areaLabel = locationLabel?.trim() || 'your area';
  const itemLabel = item?.trim() || 'gear';

  await addDoc(collection(db, 'messages'), {
    conversationId,
    conversationType: 'gear-wanted',
    wantedId,
    senderId: currentUserId,
    recipientId,
    gearName: `${GEAR_WANTED_TOPIC}: ${itemLabel}`,
    text:
      initialMessage ||
      `Hi! I saw your gear wanted post for ${itemLabel} near ${areaLabel} and may be able to help.`,
    timestamp: new Date().toISOString(),
    participants: [currentUserId, recipientId],
  });

  return conversationId;
}

export async function findOrStartGearConversation({
  currentUserId,
  recipientId,
  gearId,
  gearName,
  initialMessage,
}) {
  if (!currentUserId || !recipientId || !gearId) {
    throw new Error('Missing required conversation details.');
  }

  if (currentUserId === recipientId) {
    throw new Error('You cannot message yourself.');
  }

  const messagesQuery = query(
    collection(db, 'messages'),
    where('participants', 'array-contains', currentUserId),
  );
  const snapshot = await getDocs(messagesQuery);

  let existingConvId = null;
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.gearId !== gearId) return;

    const otherUserId = data.senderId === currentUserId ? data.recipientId : data.senderId;
    if (otherUserId === recipientId) {
      existingConvId = data.conversationId || docSnap.id;
    }
  });

  if (existingConvId) return existingConvId;

  const conversationId = `conv-gear-${gearId}-${[currentUserId, recipientId].sort().join('-')}`;
  await addDoc(collection(db, 'messages'), {
    conversationId,
    senderId: currentUserId,
    recipientId,
    gearId,
    gearName: gearName || 'Gear',
    text: initialMessage || `Hi! I'm interested in ${gearName || 'this gear'}.`,
    timestamp: new Date().toISOString(),
    participants: [currentUserId, recipientId],
  });

  return conversationId;
}

export async function findOrStartLocalExpertConversation({
  currentUserId,
  expertId,
  expertLocation,
  initialMessage,
}) {
  if (!currentUserId || !expertId) {
    throw new Error('Missing required conversation details.');
  }

  if (currentUserId === expertId) {
    throw new Error('You cannot message yourself.');
  }

  const expertSnap = await getDoc(doc(db, 'users', expertId));
  if (!expertSnap.exists() || !expertSnap.data().localExpert) {
    throw new Error('This member is not available as a local expert right now.');
  }

  const messagesQuery = query(
    collection(db, 'messages'),
    where('participants', 'array-contains', currentUserId),
  );
  const snapshot = await getDocs(messagesQuery);

  let existingConvId = null;
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.conversationType !== 'local-expert') return;

    const otherUserId = data.senderId === currentUserId ? data.recipientId : data.senderId;
    if (otherUserId === expertId) {
      existingConvId = data.conversationId || docSnap.id;
    }
  });

  if (existingConvId) return existingConvId;

  const conversationId = `conv-expert-${[currentUserId, expertId].sort().join('-')}`;
  const areaLabel = expertLocation?.trim() || 'the area';

  await addDoc(collection(db, 'messages'), {
    conversationId,
    conversationType: 'local-expert',
    senderId: currentUserId,
    recipientId: expertId,
    gearName: LOCAL_EXPERT_TOPIC,
    expertLocation: expertLocation?.trim() || '',
    text:
      initialMessage ||
      `Hi! I'd love your local tips on activities and spots around ${areaLabel}.`,
    timestamp: new Date().toISOString(),
    participants: [currentUserId, expertId],
  });

  return conversationId;
}